import React, { useState, useEffect, useCallback } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { TrendingUp, RefreshCw } from 'lucide-react';
import { reportsAPI } from '@/lib/api';

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtNum = (n) => Number(n || 0).toLocaleString('en-IN');

const today = () => new Date().toISOString().slice(0, 10);
const weekStart = () => {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay());
  return d.toISOString().slice(0, 10);
};
const monthStart = () => `${new Date().toISOString().slice(0, 7)}-01`;
const nDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const yearStart = () => `${new Date().getFullYear()}-01-01`;

// Exported so pages that need a single page-wide period toggle (e.g. Sales
// History) can drive both this chart and their own data fetches from the
// same period definitions instead of duplicating them.
export const PERIODS = [
  { label: 'Today',      start: () => today(),      end: () => today() },
  { label: 'This Week',  start: weekStart,           end: today },
  { label: 'This Month', start: monthStart,          end: today },
  { label: 'Last 90 Days', start: () => nDaysAgo(90), end: today },
  { label: 'This Year',  start: yearStart,           end: today },
  { label: 'Custom',     start: null,                end: null },
];
export const CUSTOM_PERIOD_INDEX = PERIODS.length - 1;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg shadow-md px-3 py-2 text-sm">
      <p className="text-muted-foreground mb-1">{label}</p>
      <p className="font-semibold text-emerald-600">{fmt(payload[0]?.value)}</p>
    </div>
  );
};

// Reusable "Sales Overview" chart card — used uncontrolled (manages its own
// period/date-range + shows its own period buttons and totals tiles) on the
// Dashboard, and controlled (startDate/endDate driven by a parent's own
// page-wide range picker, own tiles/controls hidden) atop Sales History.
export default function SalesOverviewChart({
  compact = false,
  defaultPeriod = 1,
  startDate,
  endDate,
  refreshToken,
  showPeriodControls = true,
  showTotals = true,
  onTotalsChange,
  onRefresh,
  headerActions,
}) {
  const controlled = startDate != null && endDate != null;

  const [chartData, setChartData]     = useState([]);
  const [chartTotals, setChartTotals] = useState({ revenue: 0, transactions: 0 });
  const [chartLoading, setChartLoading] = useState(false);
  const [activePeriod, setActivePeriod] = useState(defaultPeriod);
  const [customStart, setCustomStart] = useState(monthStart());
  const [customEnd, setCustomEnd]     = useState(today());

  const getRange = useCallback(() => {
    if (controlled) return { start: startDate, end: endDate };
    const p = PERIODS[activePeriod];
    if (p.start && p.end) return { start: p.start(), end: p.end() };
    return { start: customStart, end: customEnd };
  }, [controlled, startDate, endDate, activePeriod, customStart, customEnd]);

  const loadChart = useCallback(async () => {
    setChartLoading(true);
    try {
      const { start, end } = getRange();
      const res = await reportsAPI.getSalesReport(start, end);
      const d = res.data;
      setChartData(d.daily_breakdown || []);
      setChartTotals({ revenue: d.total_revenue, transactions: d.total_transactions });
    } catch {
      // silently ignore
    } finally {
      setChartLoading(false);
    }
  }, [getRange]);

  useEffect(() => { loadChart(); }, [loadChart, refreshToken]);

  useEffect(() => {
    onTotalsChange?.(chartTotals);
  }, [chartTotals, onTotalsChange]);

  const formatXAxis = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className={`bg-card rounded-xl border border-border ${compact ? 'p-3' : 'p-5'}`}>
      <div className={`flex items-center justify-between ${compact ? 'mb-2' : 'mb-4'}`}>
        <h2 className={`font-semibold flex items-center gap-2 ${compact ? 'text-sm' : 'text-lg'}`}>
          <TrendingUp className={compact ? 'h-4 w-4 text-primary' : 'h-5 w-5 text-primary'} />
          Sales Overview
        </h2>
        <div className="flex items-center gap-2">
          {headerActions}
          <button onClick={() => { loadChart(); onRefresh?.(); }} disabled={chartLoading} className="text-muted-foreground hover:text-foreground transition-colors">
            <RefreshCw className={`h-4 w-4 ${chartLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Period Buttons */}
      {showPeriodControls && (
        <div className={`flex flex-wrap gap-2 ${compact ? 'mb-2' : 'mb-4'}`}>
          {PERIODS.map((p, i) => (
            <button
              key={p.label}
              onClick={() => setActivePeriod(i)}
              className={`rounded-lg font-medium transition-colors ${compact ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm'} ${
                activePeriod === i
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/70'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}

      {/* Custom date pickers */}
      {showPeriodControls && activePeriod === CUSTOM_PERIOD_INDEX && (
        <div className={`flex flex-wrap gap-2 text-sm ${compact ? 'mb-2' : 'mb-4'}`}>
          <label className="flex items-center gap-2 text-muted-foreground">
            From
            <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
              className="border border-border rounded-md px-2 py-1 text-foreground" />
          </label>
          <label className="flex items-center gap-2 text-muted-foreground">
            To
            <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
              className="border border-border rounded-md px-2 py-1 text-foreground" />
          </label>
        </div>
      )}

      {/* Summary tiles */}
      {showTotals && (
        <div className={`flex gap-4 ${compact ? 'mb-2' : 'mb-4'}`}>
          <div className={`bg-emerald-50 rounded-lg ${compact ? 'px-3 py-1' : 'px-4 py-2'}`}>
            <p className="text-xs text-muted-foreground">Total Revenue</p>
            <p className={`font-bold text-emerald-600 ${compact ? 'text-sm' : 'text-lg'}`}>{fmt(chartTotals.revenue)}</p>
          </div>
          <div className={`bg-muted rounded-lg ${compact ? 'px-3 py-1' : 'px-4 py-2'}`}>
            <p className="text-xs text-muted-foreground">Transactions</p>
            <p className={`font-bold text-foreground ${compact ? 'text-sm' : 'text-lg'}`}>{fmtNum(chartTotals.transactions)}</p>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className={compact ? 'h-28' : 'h-52'}>
        {chartLoading ? (
          <div className="h-full flex items-center justify-center text-muted-foreground text-sm">Loading chart…</div>
        ) : chartData.length === 0 ? (
          <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No sales data for this period</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#16a34a" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#16a34a" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tickFormatter={formatXAxis} tick={{ fontSize: 11, fill: '#888' }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#888' }} tickLine={false} axisLine={false} tickFormatter={v => `₹${v}`} width={55} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="revenue" stroke="#16a34a" strokeWidth={2} fill="url(#revenueGrad)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
